// Log Activity form config + validation — a faithful port of CATS / blankForm /
// validate() from the prototype (CurrentWeek.dc.html). See
// .context/gameplan-extract/log-form.js. Drives LogActivityModal.
import type { ActivityCategory, LogActivityDto } from '@/types/gamePlanTracker';

export interface CatConfig {
  key: ActivityCategory;
  label: string;
  orgLabel: string;
  orgPh: string;
  nameLabel: string;
  nameRequired: boolean;
  reachHint?: string;
  titleLabel: string;
  titlePh: string;
  typeLabel: string;
  types: string[];
  outcomeLabel: string;
  outcomes: string[];
  nextPh: string;
  notesPh: string;
  isEvent?: boolean;
}

export const CAT_KEYS: ActivityCategory[] = [
  'hs',
  'college',
  'corporate',
  'golf',
  'event',
  'other',
];

export const CATS: Record<ActivityCategory, CatConfig> = {
  hs: {
    key: 'hs',
    label: 'High School',
    orgLabel: 'School',
    orgPh: 'School name',
    nameLabel: 'Contact name',
    nameRequired: true,
    reachHint: 'Add a phone or email so you can reach them again.',
    titleLabel: 'Contact title',
    titlePh: 'e.g. Head Golf Coach',
    typeLabel: 'Type of activity',
    types: ['In-person visit', 'Phone call', 'Email'],
    outcomeLabel: 'Where it stands',
    outcomes: ['Contacted', 'Meeting held', 'Partnership agreed'],
    nextPh: 'Send the practice-block offer to the coach',
    notesPh: 'What you discussed, what the team needs, who else was there',
  },
  college: {
    key: 'college',
    label: 'College',
    orgLabel: 'College or university',
    orgPh: 'College or university name',
    nameLabel: 'Contact name',
    nameRequired: true,
    reachHint: 'Add a phone or email so you can reach them again.',
    titleLabel: 'Contact title',
    titlePh: 'e.g. Head Coach',
    typeLabel: 'Type of activity',
    types: ['In-person visit', 'Phone call', 'Email'],
    outcomeLabel: 'Where it stands',
    outcomes: ['Contacted', 'Meeting held', 'Partnership agreed'],
    nextPh: 'Confirm the athlete membership details',
    notesPh: 'What you discussed, eligibility questions, roster size',
  },
  corporate: {
    key: 'corporate',
    label: 'Corporate',
    orgLabel: 'Company',
    orgPh: 'Company name',
    nameLabel: 'Contact name',
    nameRequired: true,
    reachHint: 'Add a phone or email so you can reach them again.',
    titleLabel: 'Contact title',
    titlePh: 'e.g. HR Director',
    typeLabel: 'Type of activity',
    types: ['In-person visit', 'Phone call', 'Email'],
    outcomeLabel: 'Where it stands',
    outcomes: ['Contacted', 'Visited', 'Session booked'],
    nextPh: 'Follow up within 6 hours with the corporate offer',
    notesPh: 'Team size, budget line, what they do for outings today',
  },
  golf: {
    key: 'golf',
    label: 'Golf Course',
    orgLabel: 'Golf course',
    orgPh: 'Golf course name',
    nameLabel: 'Contact name',
    nameRequired: true,
    reachHint: 'Add a phone or email so you can reach them again.',
    titleLabel: 'Contact title',
    titlePh: 'e.g. Head Professional',
    typeLabel: 'Type of activity',
    types: ['In-person visit', 'Phone call', 'Email'],
    outcomeLabel: 'Where it stands',
    outcomes: ['Contacted', 'Met the pro shop', 'Perk trade agreed', 'Co-host event planned'],
    nextPh: 'Propose a member perk trade',
    notesPh: 'Who you met, their off-season plans, ideas for co-hosting',
  },
  event: {
    key: 'event',
    label: 'Event',
    isEvent: true,
    orgLabel: 'Event name',
    orgPh: 'Tournament or event name',
    nameLabel: 'Organizer name',
    nameRequired: false,
    reachHint: 'Organizer phone or email is optional for events.',
    titleLabel: 'Organizer title',
    titlePh: 'e.g. Tournament Director',
    typeLabel: 'Type of involvement',
    types: ['Sponsorship', 'Table or booth', 'Attended', 'Reached out'],
    outcomeLabel: 'Status',
    outcomes: ['Inquiry', 'Booked', 'Completed'],
    nextPh: 'Follow up with every lead from the table within 6 hours',
    notesPh: 'Who came by, what worked at the table, whether to sponsor again',
  },
  other: {
    key: 'other',
    label: 'Other',
    orgLabel: 'Organization',
    orgPh: 'Business, group, or organization name',
    nameLabel: 'Contact name',
    nameRequired: true,
    reachHint: 'Add a phone or email so you can reach them again.',
    titleLabel: 'Contact title',
    titlePh: 'e.g. Owner',
    typeLabel: 'Type of activity',
    types: ['In-person visit', 'Phone call', 'Email'],
    outcomeLabel: 'Where it stands',
    outcomes: ['Contacted', 'Meeting held', 'Interested'],
    nextPh: 'What you will do next',
    notesPh: 'What you discussed and why it matters',
  },
};

export interface LogFormState {
  cat: ActivityCategory | null;
  type: string;
  date: string;
  org: string;
  name: string;
  title: string;
  phone: string;
  email: string;
  location: string;
  eventDate: string;
  cost: string;
  leads: string;
  outcome: string;
  nextStep: string;
  followUp: string;
  notes: string;
}

export function blankForm(todayISO: string): LogFormState {
  return {
    cat: null,
    type: '',
    date: todayISO,
    org: '',
    name: '',
    title: '',
    phone: '',
    email: '',
    location: '',
    eventDate: '',
    cost: '',
    leads: '',
    outcome: '',
    nextStep: '',
    followUp: '',
    notes: '',
  };
}

export type LogFormErrors = Partial<Record<keyof LogFormState | 'contact', string>>;

const blank = (v: unknown) => !String(v == null ? '' : v).trim();
const isDate = (v: unknown) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ''));

// Faithful port of validate(fm). `todayISO`/`planStartISO` scope the dates;
// `isUpcoming` relaxes the plan-start floor before the plan has begun.
export function validateLogForm(
  fm: LogFormState,
  opts: { todayISO: string; planStartISO: string; isUpcoming: boolean },
): LogFormErrors {
  const { todayISO, planStartISO, isUpcoming } = opts;
  const c = fm.cat ? CATS[fm.cat] : null;
  const e: LogFormErrors = {};
  if (!c) {
    e.cat = 'Choose a category.';
    return e;
  }
  if (blank(fm.date) || !isDate(fm.date)) e.date = 'Add the date of contact.';
  else if (fm.date > todayISO)
    e.date = "Can't be in the future. Log it after it happens.";
  else if (!isUpcoming && fm.date < planStartISO)
    e.date = 'Pick a date on or after your plan started.';
  if (blank(fm.type)) e.type = 'Choose one.';
  if (blank(fm.org)) e.org = 'Add the ' + c.orgLabel.toLowerCase() + '.';
  else if (fm.org.trim().length < 2)
    e.org = 'Enter the full ' + c.orgLabel.toLowerCase() + '.';
  if (c.nameRequired) {
    if (blank(fm.name)) e.name = 'Add who you spoke with.';
    else if (fm.name.trim().length < 2) e.name = 'Enter their full name.';
  }
  const digits = String(fm.phone || '').replace(/\D/g, '');
  if (
    !blank(fm.phone) &&
    !(digits.length === 10 || (digits.length === 11 && digits[0] === '1'))
  )
    e.phone = 'Enter a 10-digit phone number.';
  if (
    !blank(fm.email) &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(fm.email.trim())
  )
    e.email = 'Enter a valid email, like name@company.com.';
  if (c.nameRequired && blank(fm.phone) && blank(fm.email))
    e.contact = 'Add a phone or email.';
  if (fm.type === 'Email' && blank(fm.email) && !e.contact)
    e.email = 'Add the email you contacted.';
  if (fm.type === 'Phone call' && blank(fm.phone) && !e.contact)
    e.phone = 'Add the number you called.';
  if (c.isEvent) {
    const needsEventDate = fm.type !== 'Reached out';
    if (needsEventDate && (blank(fm.eventDate) || !isDate(fm.eventDate)))
      e.eventDate = 'Add the event date.';
    else if (isDate(fm.eventDate)) {
      if (
        (fm.type === 'Attended' || fm.outcome === 'Completed') &&
        fm.eventDate > todayISO
      )
        e.eventDate =
          fm.type === 'Attended'
            ? "You can't have attended a future event."
            : "A completed event can't be in the future.";
      else if (!isUpcoming && fm.eventDate < planStartISO)
        e.eventDate = 'Pick a date on or after your plan started.';
    }
    if (!blank(fm.cost) && !(Number(fm.cost) >= 0 && Number(fm.cost) <= 100000))
      e.cost = 'Enter a dollar amount, 0 or more.';
    if (
      !blank(fm.leads) &&
      !(
        Number.isInteger(Number(fm.leads)) &&
        Number(fm.leads) >= 0 &&
        Number(fm.leads) <= 5000
      )
    )
      e.leads = 'Enter a whole number, 0 or more.';
  }
  if (blank(fm.nextStep)) e.nextStep = 'Add the next step.';
  if (!blank(fm.followUp)) {
    if (!isDate(fm.followUp)) e.followUp = 'Enter a valid date.';
    else if (fm.followUp < todayISO)
      e.followUp = "Follow-up can't be in the past. Pick today or later.";
    else if (!e.date && isDate(fm.date) && fm.followUp < fm.date)
      e.followUp = "Follow-up can't be before the contact date.";
  }
  if (blank(fm.notes))
    e.notes = "Add notes. They're what make the follow-up easy.";
  else if (fm.notes.trim().length < 10)
    e.notes = 'Add a bit more detail so you can pick this up later.';
  return e;
}

export function toLogDto(fm: LogFormState): LogActivityDto {
  return {
    category: fm.cat as ActivityCategory,
    type: fm.type || undefined,
    date: fm.date,
    org: fm.org.trim() || undefined,
    name: fm.name.trim() || undefined,
    title: fm.title.trim() || undefined,
    phone: fm.phone.trim() || undefined,
    email: fm.email.trim() || undefined,
    location: fm.location.trim() || undefined,
    eventDate: fm.eventDate || undefined,
    cost: fm.cost ? Number(fm.cost) : undefined,
    leads: fm.leads ? Number(fm.leads) : undefined,
    outcome: fm.outcome || undefined,
    nextStep: fm.nextStep.trim() || undefined,
    followUp: fm.followUp || undefined,
    notes: fm.notes.trim() || undefined,
  };
}
